import { useEffect, useState } from "react";
import { Button, Checkbox, Message, Tooltip } from "@humansignal/ui";
import { useAPI } from "@humansignal/core";
import { useAuth } from "@humansignal/core/providers/AuthProvider";
import { cn } from "../../utils/bem";
import "./HFImportPanel.prefix.css";

/**
 * Hugging Face Hub import panel — reused by two surfaces:
 *
 *   1. CreateProject wizard → Import step → "Hugging Face" tab.
 *   2. Project Settings → Hugging Face Hub tab (Import card).
 *
 * Takes a ``projectId`` (draft or persisted, the endpoint doesn't care)
 * and calls the existing ``/api/projects/<pk>/hub/{preview,import}/``
 * endpoints. Everything on-panel is stateful locally; on successful
 * import it calls ``onImported`` with the response so the parent can
 * update source binding / advance the wizard / etc.
 *
 * If the caller's HF token isn't set, a small prompt links out (or
 * calls ``onNeedToken`` if the parent wants to open a modal).
 */

const p = cn("hf-import-panel");
const px = (elem) => p.elem(elem).toClassName();

export function HFImportPanel({ projectId, onImported, onNeedToken, requiredKeys = [], labelConfig }) {
  const { user } = useAuth();
  const api = useAPI();
  // Effective access comes from the server: the member's own token, else
  // the organisation's token when their role (or a grant) allows import.
  const [access, setAccess] = useState(null);
  useEffect(() => {
    if (!projectId) return;
    let alive = true;
    api.callApi("hfAccess", { params: { pk: projectId }, errorFilter: () => true }).then((res) => {
      if (alive)
        setAccess(
          res && !res.error ? res : { import: false, message: "Hugging Face is not available for this project." },
        );
    });
    return () => {
      alive = false;
    };
  }, [api, projectId, user?.has_hf_token]);
  const hasToken = access ? !!access.import : !!user?.has_hf_token;

  const [datasetId, setDatasetId] = useState("");
  const [config, setConfig] = useState("");
  const [split, setSplit] = useState("train");
  const [limit, setLimit] = useState("");
  const [preview, setPreview] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [mapping, setMapping] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [imported, setImported] = useState(null);

  const canPreview = hasToken && !!datasetId.trim() && !!projectId;
  // The task fields the chosen columns will fill (a column keeps its own
  // name unless another is typed beside it), against the fields the
  // labeling setup shows. The server refuses the same gap.
  const provided = new Set(Array.from(selected, (c) => (mapping[c] || "").trim() || c));
  const stillNeeded = requiredKeys.filter((k) => !provided.has(k));
  const canImport = canPreview && selected.size > 0 && stillNeeded.length === 0;

  // Track which field the error belongs to so we can red-outline the
  // right input. Currently only "datasetId" (bad repo / access denied)
  // is a field-scoped error; anything else is treated as generic and
  // shows only as the Message banner.
  const [errorField, setErrorField] = useState(null);

  // Replaced by the field detection inside doPreview/doImport — kept
  // as a no-op stub for symmetry with the earlier import-flow call
  // site that also sets error state.
  const inferErrorField = () => null;

  const doPreview = async () => {
    setBusy(true);
    setError(null);
    setErrorField(null);
    setImported(null);
    const res = await api.callApi("hfPreview", {
      params: { pk: projectId },
      body: { dataset_id: datasetId.trim(), config: config.trim() || undefined, split: split.trim() || "train" },
      // Prevent the platform-wide error modal / toast; we render the
      // failure inline instead so the user can retype without losing
      // the panel state.
      suppressError: true,
    });
    setBusy(false);
    const failed = !res || res.error || res.$meta?.ok === false;
    if (failed) {
      const raw = res?.response?.detail || res?.error || res?.$meta?.status || "";
      // Common cases in plain English + always echo the raw server
      // detail below (when we have one) so the user has something to
      // search for.
      const upper = String(raw);
      let msg;
      let field = null;
      // Backend prefixes errors with explicit tokens so we don't have
      // to guess from natural-language strings. See
      // data_import/hf_hub_import.preview_hub_dataset.
      if (upper.startsWith("DATASET_UNREACHABLE") || !raw) {
        msg = `No Hugging Face dataset named "${datasetId.trim()}" is publicly available. Please make sure the name (owner/name) is correct, and, if this is a gated or private dataset, that you have access to it.`;
        field = "datasetId";
      } else if (upper.startsWith("CONFIG_INVALID")) {
        const cfg = config.trim();
        msg = cfg
          ? `The config "${cfg}" isn't one of the ones this dataset ships. Check the available configs on the dataset card, or leave the field blank for the default.`
          : `That config isn't available. Check the configs listed on the dataset card, or leave the field blank for the default.`;
        field = "config";
      } else if (upper.startsWith("SPLIT_INVALID") || upper.startsWith("SPLIT_UNREACHABLE")) {
        const sp = split.trim();
        msg = sp
          ? `The split "${sp}" isn't available for this config. Check the splits listed on the dataset card (usually train / validation / test).`
          : `That split isn't available. Check the ones listed on the dataset card.`;
        field = "split";
      } else {
        msg = `Preview didn't return anything usable: ${raw}`;
      }
      setError(msg);
      setErrorField(field);
      setPreview(null);
      return;
    }
    setPreview(res);
    setConfig(res.chosen_config || "");
    setSplit(res.chosen_split || "train");
    setSelected(new Set((res.columns || []).map((c) => c.name)));
  };

  const doImport = async () => {
    setBusy(true);
    setError(null);
    setErrorField(null);
    const trimmedMapping = {};
    for (const c of selected) {
      const to = (mapping[c] || "").trim();
      if (to && to !== c) trimmedMapping[c] = to;
    }
    const res = await api.callApi("hfImport", {
      params: { pk: projectId },
      body: {
        dataset_id: datasetId.trim(),
        config: config.trim() || undefined,
        split: split.trim() || "train",
        columns: Array.from(selected),
        limit: limit ? Number(limit) : undefined,
        column_mapping: Object.keys(trimmedMapping).length ? trimmedMapping : undefined,
        // Judged against the setup as it is now (newer than the saved one
        // in the create wizard).
        label_config: labelConfig || undefined,
      },
      suppressError: true,
    });
    setBusy(false);
    if (!res || res.error || res.$meta?.ok === false) {
      const detail = res?.response?.detail || res?.error || "Import failed. Try again or reduce the row limit.";
      setError(detail);
      setErrorField(inferErrorField(detail));
      return;
    }
    setImported(res);
    onImported?.(res);
  };

  if (access === null && projectId) {
    return (
      <div className={p.toClassName()}>
        <div className={px("empty")}>
          <p>Checking Hugging Face access…</p>
        </div>
      </div>
    );
  }

  if (!hasToken) {
    // Disabled state: say plainly who can switch it on. Owners and admins
    // get the organisation settings link; everyone else is told to ask,
    // with a personal token as the fallback.
    return (
      <div className={p.toClassName()} aria-disabled="true">
        <div className={px("empty")}>
          <p>{access?.message ?? "Hugging Face is not set up for this organisation."}</p>
          <div className={px("actions")}>
            {access?.can_manage_org_token && (
              <a href="/organization/settings?tab=Hugging%20Face" className={px("link")}>
                Set up for the organisation →
              </a>
            )}
            {onNeedToken ? (
              <Button type="button" look="outlined" size="small" onClick={onNeedToken}>
                Use my own token
              </Button>
            ) : (
              <a href="/user/account?section=hugging-face" className={px("link")}>
                Use my own token →
              </a>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={p.toClassName()}>
      {access?.source === "organization" && (
        <p className={px("source-note")}>Using the organisation's Hugging Face token.</p>
      )}
      <div className={px("grid")}>
        <label className={px("field")}>
          <span className={px("label")}>Dataset id</span>
          <input
            type="text"
            className={cn("input-ls").toClassName()}
            placeholder="e.g. masakhane/afriqa"
            value={datasetId}
            onChange={(e) => {
              setDatasetId(e.target.value);
              if (errorField === "datasetId") {
                setErrorField(null);
                setError(null);
              }
            }}
            disabled={busy}
            aria-invalid={errorField === "datasetId" || undefined}
            aria-describedby={errorField === "datasetId" ? "hf-preview-error" : undefined}
          />
        </label>
        <label className={px("field")}>
          <span className={px("label")}>Config</span>
          <input
            type="text"
            className={cn("input-ls").toClassName()}
            placeholder="default"
            value={config}
            onChange={(e) => {
              setConfig(e.target.value);
              if (errorField === "config") {
                setErrorField(null);
                setError(null);
              }
            }}
            disabled={busy}
            aria-invalid={errorField === "config" || undefined}
          />
        </label>
        <label className={px("field")}>
          <span className={px("label")}>Split</span>
          <input
            type="text"
            className={cn("input-ls").toClassName()}
            placeholder="train"
            value={split}
            onChange={(e) => {
              setSplit(e.target.value);
              if (errorField === "split") {
                setErrorField(null);
                setError(null);
              }
            }}
            disabled={busy}
            aria-invalid={errorField === "split" || undefined}
          />
        </label>
        <label className={px("field")}>
          <span className={px("label")}>Limit</span>
          <input
            type="number"
            className={cn("input-ls").toClassName()}
            placeholder="e.g. 500"
            value={limit}
            onChange={(e) => setLimit(e.target.value)}
            disabled={busy}
          />
        </label>
      </div>
      <div className={px("btnrow")}>
        <Button type="button" look="outlined" size="small" onClick={doPreview} disabled={!canPreview || busy}>
          {busy && !preview ? "Loading…" : preview ? "Refresh preview" : "Preview"}
        </Button>
      </div>
      {error && (
        <Message id="hf-preview-error" look="danger">
          {error}
        </Message>
      )}
      {imported && (
        <Message look="success">
          Imported <strong>{imported.imported}</strong> rows from <code>{imported.dataset_id}</code>.
        </Message>
      )}
      {preview && (
        <div className={px("preview")}>
          <div className={px("preview-meta")}>
            <strong>{preview.dataset_id}</strong>, configs: {preview.configs.join(", ") || "—"}, splits:{" "}
            {preview.splits.join(", ") || "—"}, rows: {preview.num_rows ?? "unknown"}
          </div>
          <div className={px("mapping-title")}>
            Column mapping: check the columns to import; type a task field name to rename
          </div>
          {requiredKeys.length > 0 && (
            <div className={px("needed")} data-testid="hf-needed">
              {stillNeeded.length === 0
                ? `The labeling setup shows ${requiredKeys.join(", ")}. All provided.`
                : `Still needed: ${stillNeeded.join(", ")}. Type the field name beside the column that holds it.`}
            </div>
          )}
          <datalist id="hf-task-fields">
            {requiredKeys.map((k) => (
              <option key={k} value={k} />
            ))}
          </datalist>
          <div className={px("mapping-table")}>
            <div className={px("mapping-head")}>
              <span>Import</span>
              <span>HF column</span>
              <span>Task field</span>
              <span>Type</span>
            </div>
            {(preview.columns || []).map((c) => {
              const isSelected = selected.has(c.name);
              return (
                <div key={c.name} className={px("mapping-row")}>
                  <Checkbox
                    checked={isSelected}
                    onChange={(e) => {
                      const checked = e?.target?.checked ?? !isSelected;
                      setSelected((prev) => {
                        const next = new Set(prev);
                        if (checked) next.add(c.name);
                        else next.delete(c.name);
                        return next;
                      });
                    }}
                  />
                  <code className={px("col-name")}>{c.name}</code>
                  <input
                    type="text"
                    className={cn("input-ls").toClassName()}
                    placeholder={c.name}
                    value={mapping[c.name] ?? ""}
                    onChange={(e) => setMapping((m) => ({ ...m, [c.name]: e.target.value }))}
                    disabled={!isSelected || busy}
                    aria-label={`Task field for ${c.name}`}
                    list="hf-task-fields"
                  />
                  <Tooltip title={c.dtype}>
                    <span className={px("col-type")}>{c.dtype}</span>
                  </Tooltip>
                </div>
              );
            })}
          </div>
          <div className={px("preview-actions")}>
            <span className={px("preview-hint")}>
              {selected.size} of {preview.columns?.length ?? 0} column{selected.size === 1 ? "" : "s"} selected
            </span>
            <Button type="button" variant="primary" size="small" onClick={doImport} disabled={!canImport || busy}>
              {busy ? "Importing…" : `Import ${selected.size} column${selected.size === 1 ? "" : "s"}`}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
